import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import ControlledLocationAutocomplete from '../../components/BasicUI/ControlledInputs/ControlledLocationAutocomplete';
import BookingDateRangeFilter from '../../components/Booking/BookingDateRangeFilter/BookingDateRangeFilter';
import IconSearch from '../../components/Icons/IconSearch';
import { locationBounds } from '../../components/LocationAutocompleteInput/GeocoderMapbox';
import config from '../../config';
import { yellow } from '../../styles/mui/colors';
import { formatDateToText } from '../../util/dates';
import useScreenSize from '../../util/hooks/useScreenSize';
import { hasLocationPermission, userLocation } from '../../util/maps';
import defaultSearchCountry from './defaultSearchCountry';
import {
  HERO_DATE_RANGE_FILTER,
  HERO_DATE_RANGE_FILTER_BUTTON,
  HERO_DATE_RANGE_FILTER_BUTTON_WITH_VALUES,
  HERO_DATE_RANGE_FILTER_POPUP,
  HERO_DESKTOP_INPUT,
  HERO_DESKTOP_INPUT_ROOT,
  HeroSearchFormGlobalStyles,
  locationFormSx,
} from './heroSearchFormStyles';

// This form intentionally keeps no validation: the original Final-Form
// implementation never declared any validators and submission always fired
// regardless of field state. The schema is therefore an empty object so the
// resolver is a no-op, preserving behaviour while staying on the RHF+yup stack.
const validationSchema = yup.object().shape({});

/**
 * HeroSearchForm - location + date range search shortcut shown on hero sections.
 *
 * Behaviour notes (preserved from the Final-Form version):
 * - There is no validation; the search button always submits.
 * - The submitted value is held in a ref (`heroSearchFromState`) and is NOT the
 *   RHF form value. Selecting a place, resolving the user's geolocation, and
 *   picking dates each mutate that ref; the button reads the ref on submit.
 *   Location and geolocation are intentionally ref-only and never trigger a
 *   re-render.
 * - The `dates`/`datesLabel`/`datesLabelIsDate` triple is additionally mirrored
 *   into state (set from `onDateSubmit`) purely so the visible date-filter
 *   label and button styling re-render when a range is picked; the ref stays
 *   the single source of truth read on submit.
 * - `initialValues` is accepted for API compatibility with parents but, as in
 *   the original, it is not read into the submitted state.
 *
 * @param {object} props
 * @param {Function} props.onSubmit - called with { dates, location, datesLabelIsDate, datesLabel }
 * @param {string} [props.rootClassName]
 * @param {string} [props.className]
 * @param {string} [props.desktopInputRoot]
 * @param {object} [props.initialValues]
 * @param {object} [props.sx] - Additional sx merged onto the root wrapper.
 */
const HeroSearchFormComponent = ({
  onSubmit,
  rootClassName = undefined,
  className = undefined,
  desktopInputRoot = undefined,
  initialValues = undefined,
  sx = null,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const { isMobile, isTablet, isDesktop } = useScreenSize();

  const heroSearchFromState = useRef({
    dates: null,
    location: defaultSearchCountry(intl.locale),
    datesLabelIsDate: false,
    datesLabel: intl.formatMessage({ id: 'HeroSearchFrom.datesLabel' }),
  });

  // Mirrors the date-related slice of heroSearchFromState.current so picking a
  // range re-renders the visible label/button styling (see JSDoc above).
  const [dateFilterState, setDateFilterState] = useState({
    dates: null,
    datesLabelIsDate: false,
    datesLabel: intl.formatMessage({ id: 'HeroSearchFrom.datesLabel' }),
  });

  const { control } = useForm({
    defaultValues: {
      location: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const onHandleSubmit = useCallback(() => {
    onSubmit(heroSearchFromState.current);
  }, [onSubmit]);

  useEffect(() => {
    let isMounted = true;

    // Only a prefill: never open the permission prompt for it on page load (in the app the
    // onboarding message asks first).
    hasLocationPermission()
      .then((granted) => (granted ? userLocation() : null))
      .then((latlng) => {
        if (isMounted && latlng) {
          const bounds = locationBounds(latlng, config.maps.search.currentLocationBoundsDistance);
          if (bounds) {
            heroSearchFromState.current = {
              ...heroSearchFromState.current,
              location: {
                address: '',
                origin: latlng,
                bounds,
              },
            };
          }
        }
      })
      .catch(() => null);

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLocationChange = useCallback(
    (location) => {
      if (location.selectedPlace) {
        const inputElement = searchInputRef.current;
        if (inputElement && 'blur' in inputElement) {
          inputElement.blur();
        }
        heroSearchFromState.current = {
          ...heroSearchFromState.current,
          location: location.selectedPlace,
        };
        if (isMobile || isTablet) {
          onHandleSubmit();
        }
      }
    },
    [isMobile, isTablet, onHandleSubmit]
  );

  const onDateSubmit = useCallback(
    (values) => {
      const startDate = values.dates ? values.dates.split(',')[0] : null;
      const endDate = values.dates ? values.dates.split(',')[1] : null;
      const datesLabel =
        startDate && endDate
          ? `${formatDateToText(intl, startDate).date} - ${formatDateToText(intl, endDate).date}`
          : intl.formatMessage({ id: 'HeroSearchFrom.datesLabel' });

      heroSearchFromState.current = {
        ...heroSearchFromState.current,
        dates: values.dates,
        datesLabel,
        datesLabelIsDate: true,
      };
      setDateFilterState({
        dates: values.dates,
        datesLabel,
        datesLabelIsDate: true,
      });
    },
    [intl]
  );

  const datesLabel = dateFilterState.datesLabelIsDate
    ? dateFilterState.datesLabel
    : intl.formatMessage({ id: 'HeroSearchFrom.datesLabel' });

  const desktopInputRootClass = desktopInputRoot || HERO_DESKTOP_INPUT_ROOT;

  const preventFormSubmit = useCallback((e) => e.preventDefault(), []);

  const locationTextFieldProps = useMemo(
    () => ({
      placeholder: intl.formatMessage({ id: 'TopbarSearchForm.placeholder' }),
      className: HERO_DESKTOP_INPUT,
      inputRef: searchInputRef,
    }),
    [intl]
  );

  return (
    <Box className={className} component="div" sx={sx || undefined}>
      <HeroSearchFormGlobalStyles />
      <Box component="form" onSubmit={preventFormSubmit} sx={locationFormSx}>
        <Box sx={{ alignItems: 'center', display: 'flex', pl: 1.5, width: '100%' }}>
          <IconButton
            aria-label="Submit search"
            onClick={onHandleSubmit}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <IconSearch />
          </IconButton>
          <ControlledLocationAutocomplete
            className={desktopInputRootClass}
            clearOnSelect={false}
            // Old form passed closeOnBlur={!isMobile || !isTablet}; isMobile and
            // isTablet are mutually exclusive so that was always true, i.e. the
            // default close-on-blur behaviour. Keep it explicit for parity.
            closeOnBlur
            control={control}
            fullWidth
            inSearchPage={false}
            name="location"
            navigate={navigate}
            onLocationChange={handleLocationChange}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.placeholder' })}
            searchAppearance
            textFieldProps={locationTextFieldProps}
          />
        </Box>
      </Box>
      {isDesktop ? (
        <BookingDateRangeFilter
          buttonClassName={
            dateFilterState.dates
              ? HERO_DATE_RANGE_FILTER_BUTTON_WITH_VALUES
              : HERO_DATE_RANGE_FILTER_BUTTON
          }
          id="HeroSearchFormDatesPicker"
          label={datesLabel}
          onSubmit={onDateSubmit}
          popupClassName={HERO_DATE_RANGE_FILTER_POPUP}
          queryParamNames={['dates']}
          rootClassName={HERO_DATE_RANGE_FILTER}
        />
      ) : null}
      <IconButton
        aria-label="Search"
        color="secondary"
        onClick={onHandleSubmit}
        size="large"
        sx={{
          backgroundColor: yellow.main,
          mx: 2,
          '&:hover': {
            backgroundColor: 'secondary.main',
            color: 'yellow.main',
            '& .MuiSvgIcon-root': {
              color: yellow.main,
            },
          },
        }}
        type="submit"
      >
        <IconSearch strokeWidth={2.5} sx={{ color: 'secondary' }} />
      </IconButton>
    </Box>
  );
};

HeroSearchFormComponent.displayName = 'HeroSearchForm';

const HeroSearchForm = memo(HeroSearchFormComponent);

export default HeroSearchForm;
